import { Component, HostListener, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ProjectService } from 'src/app/services/project.service';
import { ActivatedRoute, Router } from '@angular/router';
import { ContactService } from 'src/app/services/contact.service';
import { ConfigService } from 'src/app/services/config.service';
import { DxDataGridModule, DxSelectBoxModule, DxTextBoxComponent, DxDropDownButtonModule, DxPopoverModule, DxListModule } from 'devextreme-angular';
import { AppGlobals } from 'src/app/app.globals';
import { environment } from 'src/environments/environment';
import { ActivityLogService } from 'src/app/services/activity-log.service';
import { ActivitySection } from 'src/app/constants/activity-log';
import { GpdButtonComponent } from 'src/app/components/gpd-button/gpd-button.component';
import { GpdPillComponent } from 'src/app/components/gpd-pill/gpd-pill.component';
import { DeletePopupComponent } from 'src/app/components/delete-popup/delete-popup.component';
import { ProjectAddContactModalComponent } from './project-add-contact-modal/project-add-contact-modal.component';
import { ProjectNewContactModalComponent } from './project-new-contact-modal/project-new-contact-modal.component';

import { SearchableDropdownDirective } from '../../../../../directives/searchable-dropdown.directive';
@Component({
    selector: 'app-project-contacts',
    imports: [
        CommonModule,
        DxDataGridModule,
        DxSelectBoxModule,
        GpdButtonComponent,
        GpdPillComponent,
        DeletePopupComponent,
        ProjectAddContactModalComponent,
        ProjectNewContactModalComponent,
        DxTextBoxComponent,
        DxDropDownButtonModule,
        DxPopoverModule,
        DxListModule,
        SearchableDropdownDirective
    ],
    templateUrl: './project-contacts.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: ['./project-contacts.component.css']
})
export class ProjectContactsComponent implements OnInit {
  contacts: any;

  projectId: number;
  projectName = '';
  isCtrlPressed: boolean;
  entriesPerPage: any;
  contactTags: any[] = [];
  private tagTitles = new Map<string, string>();
  roles: string[] = [];

  addModalVisible = false;

  newContactModalVisible = false;
  newContactType: 'person' | 'company' = 'person';

  newMenuVisible = false;

  // The popover's own outside-click handler would fire on the "New" button too, closing the
  // popover just before the button's (onClick) reopened it. Ignoring clicks on the button here
  // leaves the toggle entirely to (onClick).
  hideNewMenuOnOutsideClick = (e: any): boolean =>
    !(e?.target as HTMLElement)?.closest?.('#newContactMenuButton');

  newMenuItems = [
    { id: 'existing', text: $localize`:@@existingContact:Existing contact` },
    { id: 'person', text: $localize`:@@newPerson:New person` },
    { id: 'company', text: $localize`:@@newCompany:New company` },
  ];

  deleteVisible = false;
  deleteTitle = '';
  deleteMessage = '';
  private roleToDelete: any = null;

  deletionNotPossibleHint = $localize`:@@deletionNotPossibleHere:Deletion is not possible here. Please delete the contact where it was added.`;

  constructor(
    private appGlobals: AppGlobals,
    private contactService: ContactService,
    private configService: ConfigService,
    private projectService: ProjectService,
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private activityLog: ActivityLogService
  ) {}

  async ngOnInit(): Promise<void> {
    if (environment.isTestEnvironment === true) {
      this.appGlobals.SetGlobalMenuIndex(2);
    } else {
      this.appGlobals.SetGlobalMenuIndex(2);
    }
    this.projectId = Number(this.activatedRoute.snapshot.params['id']);
    this.activityLog.logView({ Section: ActivitySection.PROJECT_CONTACTS, ProjectId: this.projectId });
    // The tags column renders from this catalog — a silent failure would look like
    // "no contact has tags", so surface it instead of swallowing it.
    this.contactTags = await this.configService.loadContactTags().catch(error => {
      AppGlobals.ShowErrorMessage(error);
      return [];
    });
    this.tagTitles = new Map(this.contactTags.map(t => [String(t.id), t.title]));
    this.loadRoles();
    this.loadProjectName();
    this.getPageData();
  }

  private async loadRoles(): Promise<void> {
    const result = await this.contactService.getContactRoles({}).catch(() => null);
    this.roles = (result?.roles ?? []).map((r: any) => r.name).filter((n: string) => !!n);
  }

  private async loadProjectName(): Promise<void> {
    const result = await this.projectService.getProject({ ProjectId: this.projectId }).catch(() => null);
    this.projectName = result?.project?.title ?? '';
  }

  getPageData() {
    const payload = {
      projectId: this.projectId,
    };

    this.contactService.getProjectContacts({ ProjectContactRequestItem: payload }).then(result => {
      result.contactItems.forEach(item => {
        item.routingInfo.forEach(route => {
          if (route.description === 'Cost Partner') {
            route.description = $localize`:@@costPartner:Service Provider`;
          }
          // A Gesellschaft linked to the project shows up with the role "Gesellschaft".
          if (route.descriptionRouteType === 'society') {
            route.description = $localize`:@@societyRole:Society`;
          }
        });
        this.calculateEntriesPerPage();
      });

      this.contacts = result.contactItems;
    });
  }

  // Tag id -> title. Keyed by string because the grid ids arrive as numbers and the
  // catalog ids do not always agree on type — the contacts overview pages compare with ==.
  getTag(id: any): string {
    return this.tagTitles.get(String(id)) ?? '';
  }

  // Sort/filter/search value for the tags column, which renders pills instead of a plain field.
  tagsCellValue = (row: any): string =>
    (row?.tags ?? [])
      .map((t: any) => this.getTag(t))
      .filter((title: string) => !!title)
      .join(', ');

  // Every manual link the contact holds in this project — one pill per custom role.
  manualRoles(data: any): any[] {
    return data?.manualRoles ?? [];
  }

  // Sort/filter/search value for the role column, which renders pills instead of a plain field.
  roleCellValue = (row: any): string =>
    (row?.manualRoles ?? [])
      .map((m: any) => m.role)
      .filter((r: string) => !!r)
      .join(', ');

  // Derived source routing (task, cost, property, component) to show as badges.
  // Excludes the internal "manual" marker — the manual link is represented by the editable role combo.
  sourceRouting(data: any): any[] {
    return (data?.routingInfo ?? []).filter((r: any) => r.descriptionRouteType !== 'manual');
  }

  onNewMenuClick(e: any) {
    switch (e.itemData?.id) {
      case 'existing':
        this.addModalVisible = true;
        break;
      case 'person':
        this.newContactType = 'person';
        this.newContactModalVisible = true;
        break;
      case 'company':
        this.newContactType = 'company';
        this.newContactModalVisible = true;
        break;
    }
  }

  onContactsSaved() {
    this.getPageData();
    this.loadRoles();
  }

  onRemoveRole(data: any, manualRole: any) {
    // Removing a custom role deletes its Project_Contact link — always confirm first. If it
    // is the contact's last manual role and there are no derived source references left,
    // that removes the contact from the project entirely, so the message/title reflect that
    // bigger consequence; otherwise it's phrased as just removing the one role.
    const willRemoveContact = this.sourceRouting(data).length === 0 && this.manualRoles(data).length <= 1;
    this.roleToDelete = manualRole;
    if (willRemoveContact) {
      this.deleteTitle = $localize`:@@deleteContact:Delete contact`;
      this.deleteMessage = $localize`:@@deleteContactConfirm:Are you sure you want to delete ${
        '<b>' + (data.displayName ?? '') + '</b>'
      }:contact: from ${'<b>' + this.projectName + '</b>'}:project:?`;
    } else {
      this.deleteTitle = $localize`:@@deleteRole:Delete role`;
      this.deleteMessage = $localize`:@@deleteRoleConfirm:Are you sure you want to remove the role ${
        '<b>' + (manualRole.role ?? '') + '</b>'
      }:role: from ${'<b>' + (data.displayName ?? '') + '</b>'}:project:?`;
    }
    this.deleteVisible = true;
  }

  private removeManualLink(manualRole: any) {
    this.contactService
      .removeProjectContact({ ProjectContactId: manualRole.projectContactId })
      .then(result => {
        if (result?.hasError) {
          AppGlobals.ShowErrorMessage(result.feedback);
        } else {
          this.getPageData();
        }
      })
      .catch(error => AppGlobals.ShowErrorMessage(error));
  }

  onDeleteConfirmed() {
    if (!this.roleToDelete) {
      return;
    }
    this.removeManualLink(this.roleToDelete);
    this.deleteVisible = false;
    this.roleToDelete = null;
  }

  onDeleteAborted() {
    this.deleteVisible = false;
    this.roleToDelete = null;
  }

  // fcuntion to calculcate the amount of columns in the grid based on the screen height and zoom level
  // this function is used to make the grid responsive
  @HostListener('window:resize', ['$event'])
  onResize(event: any) {
    this.calculateEntriesPerPage();
  }

  calculateEntriesPerPage() {
    const windowHeight = window.innerHeight;
    const headerHeight = 750;
    const rowHeight = 40;
    const minEntriesPerPage = 5;

    let calculatedEntries = Math.floor((windowHeight - headerHeight) / rowHeight);
    this.entriesPerPage = Math.max(calculatedEntries, minEntriesPerPage);
  }

  @HostListener('document:keydown', ['$event'])
  onKeydown(event: KeyboardEvent) {
    if (event.key === 'Control' || event.key === 'Meta') {
      this.isCtrlPressed = true;
    }
  }

  @HostListener('document:keyup', ['$event'])
  onKeyup(event: KeyboardEvent) {
    if (event.key === 'Control' || event.key === 'Meta') {
      this.isCtrlPressed = false;
    }
  }

  onCellClick(e: any) {
    if (e.columnIndex == 0) {
      const path = 'contacts/' + e.data.contactType + '/' + e.data.id;
      if (!this.isCtrlPressed) {
        this.router.navigate([path]);
      } else {
        window.open(path, '_blank');
      }
    }
  }

  onItemClick(data, routeData: any) {
    const nr = routeData.descriptionRouteId;
    switch (routeData.descriptionRouteType) {
      case 'task':
        if (!this.isCtrlPressed) {
          this.router.navigate([`site/${data.siteId}/project/${data.projectId}/tasks/${nr}`]);
        } else {
          window.open(`site/${data.siteId}/project/${data.id}/tasks`);
        }
        break;

      case 'property':
        if (!this.isCtrlPressed) {
          this.router.navigate([`site/${routeData.descriptionRouteId}/properties/`]);
        } else {
          window.open(`site/${routeData.descriptionRouteId}/properties/`);
        }
        break;

      case 'gridComponent':
        if (!this.isCtrlPressed) {
          this.router.navigate([`site/${data.siteId}/gridConnectionComponents`]);
        } else {
          window.open(`site/${data.siteId}/gridConnectionComponents`);
        }
        break;

      case 'projectComponent':
        if (!this.isCtrlPressed) {
          this.router.navigate([`site/${data.siteId}/project/${data.projectId}/projectInfo/components`]);
        } else {
          window.open(`site/${data.siteId}/project/${data.projectId}/projectInfo/components`);
        }
        break;

      case 'society':
        if (!this.isCtrlPressed) {
          this.router.navigate([`contacts/society/${nr}`]);
        } else {
          window.open(`contacts/society/${nr}`);
        }
        break;

      case 'project-cost':
        if (!this.isCtrlPressed) {
          this.router.navigate([`site/${data.siteId}/project/${data.projectId}/project-costs`]);
        } else {
          window.open(`site/${data.siteId}/project/${data.projectId}/project-costs`);
        }
        break;
    }
  }
}
